import { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import history from '@history';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import WdPageSimple from '@main/core/WdPageSimple';
import {
  ButtonWithLoader,
  ComposedHeader,
  FormCardLayout,
  ImagePicker,
  MetadataExpandForm,
} from '@main/components';
import { showMessage } from 'app/store/core/messageSlice';
import { getScopes } from 'app/api/admin';
import { useWithApiValidation } from '@main/hooks/useWithApiValidation';
import { validateEmail, validatePhone } from 'app/api/validations';
import { useRequest } from '@main/hooks';
import { uploadImageFile } from 'src/app/api/image';
import { getProfile, updateProfile } from 'src/app/api/profile';
import { defaultValues } from '../Defaults/update';
import { imageDefaults } from '../Defaults/image';
import { schema } from '../Validators/update';

const Root = styled(WdPageSimple)(({ theme }) => ({
  backgroundColor: '#FFF',
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

export const Update = () => {
  const { id: previousId } = useParams();
  const Dispatch = useDispatch();
  const { finalizeSession } = useContext(BootstrapContext);
  const { config } = useSelector((state) => state.config);
  const { api } = config;
  const [metadata, setMetadata] = useState({});
  const [metadataError, setMetadataError] = useState(false);
  const [catalogScopes, setCatalogScopes] = useState([]);
  const [avatar, setAvatar] = useState(undefined);
  const [previousAvatar, setPreviousAvatar] = useState();
  const [previousPhone, setPreviousPhone] = useState();
  const [previousEmail, setPreviousEmail] = useState();
  // ----- Inicializacion de validadores de formulario
  const { control, formState, handleSubmit, setValue } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, dirtyFields, errors } = formState;
  // ------ Request Get Admin ------
  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };
  const { makeRequest: getPreviousData, response: responsePreviousData } = useRequest(getProfile, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });
  // ------ Request Update Admin ------
  const { makeRequest: tryUpdate, isLoading } = useRequest(updateProfile);
  // ------- Request Get Scopes Admin -----------
  const { makeRequest: tryGetScopes, response: responseGetScopes } = useRequest(getScopes);
  // ------ Funcion para cargar los datos obtenidos de request
  const handleLoadToForm = (previousData) => {
    setPreviousAvatar(previousData?.avatar);
    setPreviousPhone(previousData.phone);
    setPreviousEmail(previousData.email);
    const result = _.omit(previousData, [
      'updated_at',
      'created_at',
      'id',
      'avatar',
      'image',
      'metadata',
    ]);
    const keyNames = Object.keys(result);
    keyNames.forEach((keyName) => {
      setValue(keyName, result[keyName], { shouldValidate: true, shouldDirty: true });
    });
  };

  // ------- watchers scopes ---------
  useLayoutEffect(() => {
    tryGetScopes();
  }, []);

  useLayoutEffect(() => {
    if (responseGetScopes === undefined) return;
    console.log(responseGetScopes);
    setCatalogScopes(responseGetScopes);
  }, [responseGetScopes]);

  // ------ Realiza request para cargar información del admin
  useEffect(() => {
    console.log('PARAMS ', previousId);
    console.log('URLBASE: ', api?.host);
    getPreviousData(previousId);
  }, []);

  // ------ Escucha response de request de Admin
  useEffect(() => {
    console.log(responsePreviousData);
    if (!responsePreviousData) {
      return;
    }
    handleLoadToForm(responsePreviousData);
  }, [responsePreviousData]);

  useEffect(() => {
    console.log(previousAvatar);
  }, [previousAvatar]);

  // ------ handlers ------------
  const handleLoadImage = (image) => {
    setAvatar(image);
  };

  const handleUpdate = (form) => {
    const builtForm = {
      ...form,
      avatar,
      metadata,
    };
    console.log(JSON.stringify(builtForm, null, 3));
    tryUpdate(builtForm, previousId).then(({ isOk }) => {
      if (isOk) {
        Dispatch(
          showMessage({
            message: 'Información actualizada correctamente',
            variant: 'success',
          })
        );
        history.replace('/admin');
      }
    });
  };

  const {
    isUnique: uniqueEmail,
    errorMessage: errorMessageEmail,
    validateField: validateFieldEmail,
  } = useWithApiValidation('phone', validateEmail);

  const {
    isUnique: uniquePhone,
    errorMessage: errorMessagePhone,
    validateField: validateFieldPhone,
  } = useWithApiValidation('phone', validatePhone);

  return (
    <Root
      header={<ComposedHeader decorator="button" />}
      content={
        <Box
          className="flex justify-center items-start"
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <FormCardLayout title="Editar administrador">
            <FormCardLayout.FullColum>
              <ImagePicker
                onAfterUpload={handleLoadImage}
                onDeleteUpload={() => setAvatar(null)}
                baseUri={api?.host}
                storeRequest={uploadImageFile}
                formDataParams={imageDefaults}
                setImage={previousAvatar}
                replaceOnly
                maxSize={1025}
              />
            </FormCardLayout.FullColum>
            <FormCardLayout.FullColum>
              <form
                autoComplete="off"
                name="adminUpdateForm"
                noValidate
                className="flex flex-col justify-center w-full"
              >
                <div className="flex flex-col">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Nombre"
                        autoFocus
                        type="text"
                        error={!!errors.name}
                        helperText={errors?.name?.message}
                        variant="outlined"
                        required
                        fullWidth
                        inputProps={{
                          form: {
                            autocomplete: 'off',
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Correo electrónico"
                        autoFocus
                        type="email"
                        error={!!errors.email || !uniqueEmail}
                        helperText={errors?.email?.message || errorMessageEmail}
                        variant="outlined"
                        fullWidth
                        required
                        disabled
                        onChange={(current) => {
                          field.onChange(current);
                          console.log(current);
                          if (previousEmail !== current.target.value.toString()) {
                            validateFieldEmail(current);
                          }
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Número telefónico"
                        autoFocus
                        type="number"
                        error={!!errors.phone || !uniquePhone}
                        helperText={errors?.phone?.message || errorMessagePhone}
                        variant="outlined"
                        required
                        fullWidth
                        inputProps={{
                          form: {
                            autocomplete: 'off',
                          },
                        }}
                        onChange={(current) => {
                          field.onChange(current);
                          console.log(current);
                          if (previousPhone !== current.target.value.toString()) {
                            validateFieldPhone(current);
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </form>
            </FormCardLayout.FullColum>
            <FormCardLayout.FullColum>
              <div>
                <MetadataExpandForm
                  onChange={(metafields) => setMetadata(metafields)}
                  onError={(metaError) => setMetadataError(metaError)}
                  initialValue={responsePreviousData ? responsePreviousData.metadata : {}}
                />
              </div>
            </FormCardLayout.FullColum>
            <FormCardLayout.RowButton>
              <Button
                onClick={() => history.push('/admin')}
                variant="contained"
                className="w-full md:w-auto"
                color="info"
              >
                Cancelar
              </Button>
              <ButtonWithLoader
                onClick={handleSubmit(handleUpdate)}
                color="success"
                aria-label="Agregar administrador"
                variant="contained"
                disabled={_.isEmpty(dirtyFields) || !isValid || metadataError}
                type="submit"
                isLoading={isLoading}
              >
                Guardar
              </ButtonWithLoader>
            </FormCardLayout.RowButton>
          </FormCardLayout>
        </Box>
      }
    />
  );
};
